import { useAppQuery } from '@/hooks/useAppQuery';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { api } from '@/lib/api-client';
import { Permissions } from '@/models/permission';
import {
  type LanguageOption,
  type GetTranslationLanguagesResponse,
} from '@/models/translation';

import { translationsQueryKey } from './queryKeys';

export const getTranslationLanguages = async (): Promise<LanguageOption[]> => {
  const response =
    await api.get<GetTranslationLanguagesResponse>(`/v1/languages`);
  // The default is server-owned (per-language `is_default`). An empty scope has
  // no languages and no default — callers handle that empty case.
  return response.data.languages.map((language) => ({
    locale: language.language,
    default: language.is_default,
    selected: language.is_default,
    translatedPercent: language.translated_percent,
  }));
};

/**
 * Base (unscoped) query options. The game/sandbox scope is applied uniformly by
 * `useAppQuery` (hook) and `ensureAppQueryData` (loader), so both hit the same
 * cache entry. The fetch itself needs no scope args: the axios client injects
 * `game_code`/`sandbox_code`.
 */
export const getTranslationLanguagesQuery = () => ({
  queryKey: translationsQueryKey.languages(),
  queryFn: getTranslationLanguages,
});

/**
 * Sandbox-scoped languages available to any component. The query cache is the
 * store: the key carries game/sandbox (via `useAppQuery`), so switching sandbox
 * refetches and every caller shares one cached list.
 */
export const useGetTranslationLanguages = () => {
  // GET /v1/languages is gated on languages:read (admin-api router.go). Gated here
  // rather than at each caller: three components read this list — the Translations
  // page, the Supported Languages section and the item modal's Translations tab —
  // and none of them can do anything with a 403. Callers already default the data
  // to [], which is the same empty case an empty Sandbox produces.
  const canRead = usePermissionCheck({ oneOf: [Permissions.languages.read] });

  return useAppQuery({ ...getTranslationLanguagesQuery(), enabled: canRead });
};
